import axios from 'axios';
import { createApp } from 'vue';
import InlineSvg from 'vue-inline-svg';

import App from './App.vue';
import store from './core/store/store';
import router from './core/router/index';
import i18n from './core/translations/i18n';

import emitter from './core/config/emitter';
import configPrimeVue from './core/config/config-primevue';
import configElementPlus from './core/config/config-elementplus';
import WizardStepper from './views/components/WizardStepper/WizardStepper.vue';
import InlineSvgScoped from './views/components/InlineSvgScoped/InlineSvgScoped.vue';
import {
  ApplicationInsights,
  DistributedTracingModes,
} from '@microsoft/applicationinsights-web';
import globalErrors from './core/utils/global-errors';
import authService from './core/services/auth.service';
import ScrollableTabView from './views/components/ScrollableTabView/ScrollableTabView.vue';
import ListItemCheck from './views/components/ListItemCheck/ListItemCheck.vue';
import ListItemRadio from './views/components/ListItemRadio/ListItemRadio.vue';

globalErrors.init();

async function fetchDefaultClientSettings() {
  try {
    const r = await axios.get('/desktool-client/default-settings');
    if (r.status >= 300) {
      console.error(
        `Could not fetch default settings. HTTP[${r.status}] error.`
      );
      console.error(r);
      throw new Error(
        `Could not fetch default settings. HTTP[${r.status}] error.`
      );
    }
    
    return {
      UI_ENABLED: r.data.uiSettings,
      AZUREABB2C_CLIENTID: r.data.azureAdb2CClientId,
      AZUREADB2C_AUTHORITY: r.data.azureAdb2CAuthority,
      PLANT_REFRESH_INTERVAL: r.data.plantRefreshInterval,
      AZUREADB2C_KNOWN_AUTHORITY: r.data.azureAdb2CKnownAuthority,
      AZUREABB2C_FULL_ACCESS_SCOPE: r.data.azureAdb2CFullAccessScope,
      INSTRUMENTATION_KEY : r.data.instrumentationKey,
      PROFILE_URL: r.data.profileUrl,
      ENTRANCE_PLANT_ENABLED: r.data.entrancePlantEnabled
    };
  } catch (e) {
    console.error(
      `[X] There was an error trying to fetch the default settings`
    );
    console.error(e);
    throw new Error(e as any);
  }
}

function applyDefaultClientSettings(settings: any) {
  (window as any).appConfig = { ...(window as any).appConfig, ...settings };
  authService.init();
}

const loco = window.location;
const appRootUrl = `${loco.protocol}//${loco.host}${process.env.BASE_URL}`;
const configUrl = `${appRootUrl}/config.json`;

axios.get(configUrl).then(async (r) => {
  axios.defaults.baseURL = r.data.API_ENDPOINT;
  (window as any).appConfig = r.data;

  const settings = await fetchDefaultClientSettings();
  applyDefaultClientSettings(settings);

  const app = createApp(App).use(router).use(store).use(i18n);
  app.config.globalProperties.$config = (window as any).appConfig;
  app.config.globalProperties.$emitter = emitter;

  const appInsights = new ApplicationInsights({
    config: {
      instrumentationKey: settings.INSTRUMENTATION_KEY,
      enableAutoRouteTracking: true,
      disableFetchTracking: false,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      distributedTracingMode: DistributedTracingModes.W3C,
    },
  });
  appInsights.loadAppInsights();
  app.config.globalProperties.$appInsight = appInsights;
  await configPrimeVue(app);
  configElementPlus(app);
  app.component('InlineSVG', InlineSvg);
  app.component('InlineSvgScoped', InlineSvgScoped);
  app.component('WizardStepper', WizardStepper);
  app.component('ScrollableTabView', ScrollableTabView);
  app.component('ListItemRadio', ListItemRadio);
  app.component('ListItemCheck', ListItemCheck);

  app.mount('#app');
});
